import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/api";

export type CommunityRow = Record<string, any> & {
  author_id?: string | null;
  author_level?: number;
  author_signature?: string | null;
  author_is_bot?: boolean;
  author_bot_persona?: string | null;
  author_avatar_char?: string | null;
  author_avatar_grade?: "N" | "R" | "E" | "L" | null;
  author_avatar_level?: number;
  author_cosmetic?: Record<string, any> | null;
  author_mbti?: string | null;
};

/** author_id 있는 rows에 user_levels.level + 시그니처뱃지 + 봇여부 + 장착아바타(slug) + MBTI 병합 */
export async function attachLevels<T extends CommunityRow>(rows: T[]): Promise<T[]> {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const ids = [...new Set(rows.filter((r) => r && r.author_id).map((r) => r.author_id))] as string[];
  if (ids.length === 0) return rows;
  const [levelsRes, profilesRes, avatarsRes, mbtiRes, publicCosmetics] = await Promise.all([
    supabase.from("user_levels").select("user_id, level").in("user_id", ids),
    supabase.from("profiles").select("id, signature_badge, is_bot, bot_persona").in("id", ids),
    supabase.from("cosmetic_nick_avatars").select("user_id, card_key").in("user_id", ids),
    // mbti_results RLS는 본인 행만 SELECT 허용 → 타 작성자 code는 DEFINER RPC로만 조회 가능 (Step 2)
    supabase.rpc("community_authors_mbti", { p_ids: ids }),
    apiFetch<any>("/api/mobile/cosmetic/public", {
      method: "POST", body: { user_ids: ids }, cache: "no-store",
    }).catch(() => ({ cosmetics: [] })),
  ]);
  const levelMap = Object.fromEntries((levelsRes.data || []).map((l: any) => [l.user_id, l.level]));
  const profMap = Object.fromEntries((profilesRes.data || []).map((p: any) => [p.id, p]));
  // 신규 service_role 공개 API가 카드 UUID·등급·강화를 허용 필드로 투영한다.
  // 배포 순서가 어긋나도 기존 뷰(card_key)로 안전하게 폴백한다.
  const cosmeticMap = Object.fromEntries((publicCosmetics?.cosmetics || []).map((row: any) => [row.user_id, row]));
  const avatarMap = Object.fromEntries((avatarsRes.data || []).map((a: any) => {
    const [prefix, slug] = String(a.card_key || "").split(":");
    return [a.user_id, { slug: slug || null, grade: prefix === "base" ? null : prefix || null, card_key: a.card_key, enhance_level: 0 }];
  }));
  // code는 4글자 머니 MBTI로 정규화 (MBTI_SHORT_LABEL 키와 일치)
  const mbtiMap = Object.fromEntries(
    (mbtiRes.data || []).map((m: any) => [m.user_id, String(m.code || "").slice(0, 4).toUpperCase() || null])
  );
  return rows.map((r) => (r && r.author_id ? {
    ...r,
    author_level: levelMap[r.author_id] || 1,
    author_signature: profMap[r.author_id]?.signature_badge || null,
    author_is_bot: profMap[r.author_id]?.is_bot || false,
    author_bot_persona: profMap[r.author_id]?.bot_persona || null,
    author_avatar_char: cosmeticMap[r.author_id]?.slug || avatarMap[r.author_id]?.slug || "hwangdoongi",
    author_avatar_grade: cosmeticMap[r.author_id]?.grade || avatarMap[r.author_id]?.grade || null,
    author_avatar_level: cosmeticMap[r.author_id]?.enhance_level || 0,
    author_cosmetic: cosmeticMap[r.author_id] || avatarMap[r.author_id] || {
      slug: "hwangdoongi", grade: null, enhance_level: 0,
    },
    author_mbti: mbtiMap[r.author_id] || null,
  } : r));
}
